import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { Input } from '@components/core-ui/form-elements/Input';
import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { getApiErrorMessage } from '@helpers/getApiErrorMessage';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { asrProxyKeys } from 'app/queries/asrProxy/asrProxyKeys';
import { useEffect, useState } from 'react';
import { AsrProxyService } from '@pages/Admin/AsrProxyConsolePage/api/AsrProxyService';
import { createFetchAsrProviderOptionsQueryFn } from '@pages/Admin/AsrProxyConsolePage/api/fetchAsrProviderOptions';
import { buildProviderOptionDraft } from '@pages/Admin/AsrProxyConsolePage/helpers/buildProviderOptionDraft';
import { getInitialProviderOptionValue } from '@pages/Admin/AsrProxyConsolePage/helpers/getInitialProviderOptionValue';
import { getParameterDescription } from '@pages/Admin/AsrProxyConsolePage/helpers/getParameterDescription';
import { parseProviderOptionDraft } from '@pages/Admin/AsrProxyConsolePage/helpers/parseProviderOptionDraft';

const SPEAKER_COUNT_OPTIONS = ['speakers_expected', 'speaker_options'] as const;

const hasOption = (options: Record<string, string>, option: string) =>
  Object.prototype.hasOwnProperty.call(options, option);

export const DefaultParameters = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [savedDraft, setSavedDraft] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const optionsQuery = useQuery({
    queryKey: asrProxyKeys.providerOptions(),
    queryFn: createFetchAsrProviderOptionsQueryFn(call),
  });
  const knownOptions = optionsQuery.data?.known_options ?? [];

  useEffect(() => {
    if (!optionsQuery.data) {
      return;
    }
    const nextDraft = buildProviderOptionDraft(optionsQuery.data.options);
    setDraft(nextDraft);
    setSavedDraft(nextDraft);
  }, [optionsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (options: Record<string, unknown>) =>
      call(() => AsrProxyService.setProviderOptions(options), { dontRedirectOnError: true, onError: setError }),
    onSuccess: (response) => {
      const nextDraft = buildProviderOptionDraft(response.options);
      setDraft(nextDraft);
      setSavedDraft(nextDraft);
      setMessage('Global parameter defaults saved');
      void queryClient.invalidateQueries({ queryKey: asrProxyKeys.providerOptions() });
    },
  });

  const toggleOption = (option: string, enabled: boolean) => {
    setMessage('');
    setDraft((current) => {
      if (!enabled) {
        const remaining = { ...current };
        delete remaining[option];
        return remaining;
      }
      return { ...current, [option]: getInitialProviderOptionValue(option) };
    });
  };

  const save = () => {
    setMessage('');
    setError('');
    let options: Record<string, unknown>;
    try {
      options = parseProviderOptionDraft(draft);
    } catch (caught) {
      setError((caught as Error).message);
      return;
    }
    saveMutation.mutate(options);
  };

  const isLoading = optionsQuery.isFetching || saveMutation.isPending;
  const displayedError = error || getApiErrorMessage(optionsQuery.error);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(savedDraft);
  const hasSpeakerCountConflict = SPEAKER_COUNT_OPTIONS.every((option) => hasOption(draft, option));

  return (
    <section className="rounded-[10px] bg-surface p-5 shadow-[0_8px_10px_var(--color-shadow)]">
      <h2 className="text-lg font-medium text-foreground">Global parameter defaults</h2>
      <p className="mt-1 max-w-4xl text-sm text-foreground-muted">
        Enabled values are merged into every transcription request. Request values override these defaults key by key.
        Universal-2 removes ignored options and validates usable values before building the AssemblyAI request.
      </p>

      {message || displayedError ? (
        <div className={`mt-4 text-sm ${displayedError ? 'text-danger' : 'text-foreground-muted'}`}>
          {displayedError || message}
        </div>
      ) : null}

      {hasSpeakerCountConflict ? (
        <div className="mt-4 rounded-md border border-danger px-3 py-2 text-sm text-danger">
          <code>speakers_expected</code> and <code>speaker_options</code> conflict Keep either an exact speaker count or
          a min max range not both
        </div>
      ) : null}

      <LinearProgressLoader active={isLoading} />
      <div className="mt-5">
        <TableWrapper>
          <Table className="min-w-[1080px] table-fixed">
            <Thead $white>
              <Tr>
                <Th className="w-[28%]">Parameter</Th>
                <Th className="w-[34%]">Description</Th>
                <Th className="w-[14%] text-center">Enabled</Th>
                <Th className="w-[24%]">Default JSON value</Th>
              </Tr>
            </Thead>
            <Tbody>
              {knownOptions.map((option) => {
                const isEnabled = hasOption(draft, option);
                const conflictingOption =
                  option === 'speakers_expected'
                    ? 'speaker_options'
                    : option === 'speaker_options'
                      ? 'speakers_expected'
                      : undefined;
                const isBlockedByConflict =
                  !isEnabled && conflictingOption !== undefined && hasOption(draft, conflictingOption);
                return (
                  <Tr key={option}>
                    <Td className="!whitespace-normal align-top py-3">
                      <code className="break-words rounded bg-page px-1.5 py-0.5 text-xs text-foreground [overflow-wrap:anywhere]">
                        {option}
                      </code>
                      {isBlockedByConflict ? (
                        <span className="ml-2 break-words text-xs text-danger">
                          conflicts with enabled {conflictingOption}
                        </span>
                      ) : null}
                    </Td>
                    <Td className="!whitespace-normal align-top py-3">
                      <p className="break-words text-sm leading-5 text-foreground-muted">
                        {getParameterDescription(option)}
                      </p>
                    </Td>
                    <Td className="align-top py-3 text-center">
                      <div className="inline-flex">
                        <CheckBox
                          name={`global-${option}`}
                          checked={isEnabled}
                          disabled={isBlockedByConflict}
                          onChange={(enabled) => toggleOption(option, enabled)}
                        />
                      </div>
                    </Td>
                    <Td className="align-top py-3">
                      <Input
                        name={`global-${option}-value`}
                        label="JSON value"
                        value={draft[option] ?? ''}
                        disabled={!isEnabled}
                        error={(isEnabled && !draft[option]) || (isEnabled && hasSpeakerCountConflict)}
                        className="form-input w-full min-w-0"
                        onChange={(event) => setDraft((current) => ({ ...current, [option]: event.target.value }))}
                      />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableWrapper>
      </div>

      <div className="mt-4 flex gap-2">
        <Button className="!w-auto" disabled={!isDirty || hasSpeakerCountConflict} onClick={save}>
          Save parameter defaults
        </Button>
        <OutlinedButton className="!w-auto" disabled={!isDirty} onClick={() => setDraft(savedDraft)}>
          Discard changes
        </OutlinedButton>
      </div>
    </section>
  );
};
